// apps/nos/client/src/hooks/useImportFileUpload.ts
//
// Single-file upload pipeline for the WorkBench Import flow. Mirrors
// useChromatogramUpload's transitions (presigning → uploading → uploaded /
// failed) but tracks one slot only and skips the verify/finalize step
// (Import's parse step is the verification — the upload row stays
// `presigned`/`uploaded` and the parse controller picks it up by id).
//
// Reuses putFileToS3 from uploads_api.ts so the XHR progress logic is one
// implementation across the codebase.

import { useCallback, useRef, useState } from 'react';
import { putFileToS3 } from '@/services/uploads_api';
import { presignImportFile } from '@/services/flavor_import_api';

export type ImportUploadStatus =
  | 'idle'
  | 'presigning'
  | 'uploading'
  | 'uploaded'
  | 'failed';

export type ImportUploadSnapshot = {
  /** Stable client-side id until the server returns the real file_upload_id. */
  localId: string;
  filename: string;
  sizeBytes: number;
  contentType: string;
  status: ImportUploadStatus;
  progressPct: number;
  /** Populated once presign returns successfully. */
  fileUploadId: string | null;
  /** Populated on `failed`. */
  errorMessage: string | null;
};

export type UseImportFileUploadResult = {
  /** Current snapshot — null when no file has been picked yet. */
  snapshot: ImportUploadSnapshot | null;
  /** Kick off presign + S3 PUT for `file`. Aborts any in-flight upload first.
   *  flavorFormulaId is optional — omitted by the New-Flavor pre-create import. */
  start: (file: File, flavorFormulaId?: number) => void;
  /** Aborts the current upload (if any) and clears the snapshot. */
  reset: () => void;
};

export function useImportFileUpload(): UseImportFileUploadResult {
  const [snapshot, setSnapshot] = useState<ImportUploadSnapshot | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setSnapshot(null);
  }, []);

  const start = useCallback((file: File, flavorFormulaId?: number) => {
    // Abort any prior in-flight upload before starting a new one.
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    const localId = `imp-${crypto.randomUUID()}`;
    const contentType = file.type || 'application/octet-stream';

    let snap: ImportUploadSnapshot = {
      localId,
      filename: file.name,
      sizeBytes: file.size,
      contentType,
      status: 'presigning',
      progressPct: 0,
      fileUploadId: null,
      errorMessage: null,
    };
    setSnapshot(snap);

    void (async () => {
      let presigned;
      try {
        presigned = await presignImportFile({
          flavorFormulaId,
          filename: file.name,
          contentType,
          sizeBytes: file.size,
          signal: controller.signal,
        });
      } catch (err) {
        if (controller.signal.aborted) return;
        snap = {
          ...snap,
          status: 'failed',
          errorMessage: err instanceof Error ? err.message : 'presign failed',
        };
        setSnapshot(snap);
        return;
      }

      snap = {
        ...snap,
        status: 'uploading',
        progressPct: 0,
        fileUploadId: presigned.fileUploadId,
      };
      setSnapshot(snap);

      try {
        await putFileToS3({
          url: presigned.presignedUrl,
          file,
          requiredHeaders: presigned.requiredHeaders,
          signal: controller.signal,
          onProgress: (pct) => {
            // Only push updates if this controller is still current — guards
            // against late progress events after the user aborted.
            if (controller.signal.aborted) return;
            snap = { ...snap, progressPct: pct };
            setSnapshot(snap);
          },
        });
      } catch (err) {
        if (controller.signal.aborted) return;
        snap = {
          ...snap,
          status: 'failed',
          errorMessage: err instanceof Error ? err.message : 'upload failed',
        };
        setSnapshot(snap);
        return;
      }

      snap = { ...snap, status: 'uploaded', progressPct: 100 };
      setSnapshot(snap);
    })();
  }, []);

  return { snapshot, start, reset };
}
